import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { AbstractControl } from '@angular/forms';
import {
  compact,
  every,
  filter,
  get,
  includes,
  isArray,
  isEmpty,
  isEqual,
  isNil,
  map,
} from 'lodash';
import { BsDropdownDirective } from 'ngx-bootstrap/dropdown';

import {
  Observable,
  observableCombineLatest,
  observableOf,
  rx,
} from '@shared/rx';
import { IFieldPreferences, SelectOption, TrackByValue } from '@shared/types';
import { asSelectOptions } from '@shared/utils';
import { ValidationService } from '@components/form';
import { trackByValue } from '@shared/utils/common-utils';
import { onControlValueChanged } from '@shared/rx-operators';
import {
  HorizontalPosition,
  VerticalPosition,
} from '@geniuspos/ui-shared/modules/xdm-shared-components/definitions/types/dropdown.type';
import { DropdownMenuService } from '@geniuspos/ui-shared/modules/xdm-shared-service/dropdown-menu/dropdown-menu.service';
import { XuxSelectMenuComponent } from '../xux-select-menu/xux-select-menu.component';
import { Subscription } from 'rxjs';
import { ENTITY_ID_PATH } from '@config/global-constants';
import { XVII18nService } from '@app/i18n/services';
import { I18n } from '@app/i18n/strings-keys';

@Component({
  selector: 'xux-input-select',
  templateUrl: 'xux-input-select.component.html',
  styleUrls: ['xux-input-select.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class XuxInputSelectComponent implements OnInit, OnDestroy {
  @ViewChild('selectBtn', { static: false }) public selectBtn: ElementRef;
  @ViewChild('dropdown', { static: false })
  public dropdown: BsDropdownDirective;
  @Input() public field: IFieldPreferences = {};
  @Input() public shouldShowDropdown: boolean = true;
  @Input()
  public defaultSelectOptions: SelectOption[];

  @Output() public initializationDone: EventEmitter<void> = new EventEmitter();
  @Output() public selectClick: EventEmitter<void> = new EventEmitter();

  public selectOptions$: Observable<SelectOption[]>;
  public selectedItemText$: Observable<string>;
  public disabled: boolean;
  public disabledTooltip$: Observable<string>;
  public trackByValue: TrackByValue = trackByValue;
  public isMenuAlreadyOpen: boolean = false;
  public useAllSitesWhenSelectAll: boolean = false;
  public allSitesMessage: string = this.i18nService.t(I18n.Sites.AllSites);

  private isNeedToResetControlValue: boolean;
  private subscriptions: Subscription[] = [];

  public constructor(
    private readonly validationService: ValidationService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    public readonly dropdownMenuService: DropdownMenuService,
    public readonly i18nService: XVII18nService,
  ) {}

  public ngOnInit(): void {
    this.disabled = get(this.field, 'widgetData.disabled', false);
    this.disabledTooltip$ = get(
      this.field,
      'widgetData.disabledTooltipObs',
      '',
    );
    this.isNeedToResetControlValue = get(
      this.field,
      'widgetData.isNeedToResetControlValue',
      false,
    );
    this.useAllSitesWhenSelectAll = get(
      this.field,
      'widgetData.useAllSitesWhenSelectAll',
      false,
    );
    this.allSitesMessage = get(
      this.field,
      'widgetData.allSitesMessage',
      this.i18nService.t(I18n.Sites.AllSites),
    );

    const itemSourceIdKeyPath = get(
      this.field,
      'widgetData.itemSourceIdKeyPath',
      ENTITY_ID_PATH,
    );

    this.selectOptions$ = observableCombineLatest([
      asSelectOptions(
        isNil(this.field.selectOptions) && !isNil(this.defaultSelectOptions)
          ? this.defaultSelectOptions
          : this.field.selectOptions,
        itemSourceIdKeyPath,
      ).pipe(
        rx.map((items) => this.prependEmptyOption(items)),
        rx.tap((items) => this.addOptionValidator(items)),
      ),
      onControlValueChanged(get(this.field, 'control')),
    ]).pipe(
      rx.map(([items, selectedItem]) =>
        map(items, ({ value, text, hidden = false }) => ({
          value,
          text,
          hidden,
          selected: isArray(selectedItem)
            ? includes(selectedItem, value)
            : isEqual(selectedItem, value),
        })),
      ),
    );

    if (get(this.field, 'widgetData.useEmptyOptionAsSelected')) {
      this.selectedItemText$ = observableOf(
        get(this.field, 'widgetData.emptyOptionText', ''),
      );
    } else {
      this.selectedItemText$ = this.selectOptions$.pipe(
        rx.map((items) => {
          if (this.useAllSitesWhenSelectAll && every(items, 'selected')) {
            return this.allSitesMessage;
          }

          return map(filter(items, 'selected'), 'text').join(', ');
        }),
      );
    }

    this.subscriptions.push(
      this.dropdownMenuService.clearContainer$.subscribe(() => {
        if (this.isNeedToResetControlValue) {
          this.field.control.setValue('');
        }

        this.isMenuAlreadyOpen = false;
        this.changeDetectorRef.markForCheck();
      }),
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  public selectClickHandler(): void {
    this.selectClick.emit();
  }

  public setValue(value): void {
    this.selectBtn.nativeElement.focus();
    const control: AbstractControl = get(this, 'field.control');
    if (!control) return;
    control.setValue(value);
    control.markAsDirty();
    control.markAsTouched();
    this.dropdownMenuService.clearContainer();
  }

  public displayMenu(event?: Event): void {
    if (!this.shouldShowDropdown) return;
    if (this.isMenuAlreadyOpen) {
      this.dropdownMenuService.clearContainer();

      return;
    }
    this.dropdownMenuService.createComponent(
      XuxSelectMenuComponent,
      {
        setValue: this.setValue.bind(this),
        selectOptions$: this.selectOptions$,
        trackByValue: this.trackByValue,
      },
      this.selectBtn.nativeElement,
      HorizontalPosition.CENTER,
      VerticalPosition.BOT,
      HorizontalPosition.LEFT,
      true,
    );
    this.isMenuAlreadyOpen = true;
    event?.stopImmediatePropagation();
  }

  private prependEmptyOption(items: SelectOption[]): SelectOption[] {
    const hideEmptyOption = get(
      this.field,
      'widgetData.hideEmptyOption',
      this.field.required,
    );
    if (hideEmptyOption) return items;
    const emptyOption = {
      text: get(this.field, 'widgetData.emptyOptionText', ''),
      value: get(this.field, 'widgetData.emptyOptionValue', null),
    };

    return [emptyOption, ...items];
  }

  private addOptionValidator(items): void {
    const control: AbstractControl = get(this, 'field.control');
    if (!control) return;
    const values = map(items, 'value');
    if (isEmpty(values)) return;
    setTimeout(() => {
      const validators = [...compact(get(this.field, 'validators', []))];
      if (!this.isNeedToResetControlValue) {
        validators.push(this.validationService.oneOf(values));
      }

      control.setValidators(validators);
      control.updateValueAndValidity();
      this.initializationDone.emit();
      this.changeDetectorRef.markForCheck();
    });
  }
}
